import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import axios from 'axios';

import icon from '../../assets/support-icon4.svg';


interface FAQ {
  _id: string;
  user: string;
  question: string;
  answer: string;
  updatedAt: string;
  fileList: {
    _id: string;
    url: string;
    originalName: string;
    savedName: string;
  }[];
}

const FAQList: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [faqList, setFaqList] = useState<FAQ[]>([]);
  const [offset, setOffset] = useState(0);
  const [limit, setLimit] = useState(5);
  const [hasMore, setHasMore] = useState(true);
  const [loading, setLoading] = useState(false);
  const [openFAQ, setOpenFAQ] = useState<string[]>([]);

  const fetchFAQList = async () => {
    if (!hasMore || loading) return;
    try {
      setLoading(true);
      const response = await axios.get('/api/faq/list', {
        params: { offset, limit },
      });
      const { data, hasMore: more, nextOffset } = response.data;
      setFaqList(prev => [...prev, ...data]);
      setOffset(nextOffset);
      setHasMore(more);
      if (limit !== 5) setLimit(5);
    } catch (error) {
      console.error('FAQ 불러오기 오류:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchFAQList();
  }, []);

  const handleOpenFAQ = (id: string) => {
    setOpenFAQ(prev => [...prev, id]);
  };
  const handleCloseFAQ = (id: string) => {
    setOpenFAQ(prev => prev.filter(faq => faq !== id));
  };

  const handleDeleteFAQ = async (id: string) => {
    if (window.confirm('해당 FAQ를 삭제하시겠습니까?')) {
      try {
        await axios.delete(`/api/faq/${id}`);
        alert('삭제되었습니다.');
        // 삭제 후 변경사항 화면에 바로 반영
        setFaqList(prev => prev.filter(faq => faq._id !== id));
        setOpenFAQ(prev => prev.filter(faq => faq !== id));
      } catch (error) {
        console.error('FAQ 삭제 실패:', error);
        alert('FAQ 삭제에 실패했습니다. 다시 시도해주세요.');
      }
    }
  };

  // 첨부파일 다운로드
  const handleFileDownload = async (savedName: string, originalName: string) => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`/api/upload-files/download/${savedName}`, {
        params: {
          usedFor: 'faq',
        },
        responseType: 'blob',
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', originalName);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (err) {
      console.error('파일 다운로드 실패:', err);
    }
  };

  return (
    <div className="faq-container">
      <div className="page-header">
        <img src={icon} alt="Mind Thermometer Icon" className="page-header__icon" />
        <h2 className="page-header__title">FAQ</h2>
        <p>자주 묻는 질문을 확인하세요.</p>
      </div>
      <div className="faq-list">
        <div className="faq-list__header">
          {user?.role === 'admin' && (
            <Link to="/setting/faq/new" style={{ textDecoration: 'none' }}>
              <button>FAQ 작성</button>
            </Link>
          )}
        </div>

        {faqList.length > 0 && (
          <div className="faq-list__list">
            {faqList.map(faq => (
              <div className="item" key={faq._id}>
                <div
                  className="question-header"
                  onClick={() => {
                    if (openFAQ.includes(faq._id)) {
                      handleCloseFAQ(faq._id);
                    } else {
                      handleOpenFAQ(faq._id);
                    }
                  }}
                  style={{ cursor: 'pointer' }}
                >
                  <h3 className="question">{faq.question}</h3>
                  <span className={`toggle-icon ${openFAQ.includes(faq._id) ? 'open' : 'closed'}`}>
                    {openFAQ.includes(faq._id) ? '−' : '+'}
                  </span>
                </div>

                <div className={`answer ${openFAQ.includes(faq._id) ? 'open' : 'closed'}`}>
                  <div className="answer-content-container">
                    <div
                      className="answer-content"
                      dangerouslySetInnerHTML={{ __html: faq.answer }}
                    />
                    {user?.role === 'admin' && (
                      <div className="admin-actions">
                        <button
                          className="edit-btn"
                          onClick={() => navigate(`/setting/faq/${faq._id}`)}
                        >
                          {' '}
                          수정
                        </button>
                        <button className="delete-btn" onClick={() => handleDeleteFAQ(faq._id)}>
                          삭제
                        </button>
                      </div>
                    )}
                  </div>
                  {faq.fileList && faq.fileList.length > 0 && (
                    <div className="attachments">
                      {faq.fileList.map((file: any) => (
                        <button
                          className="file-btn"
                          key={file._id}
                          rel="noopener noreferrer"
                          onClick={() => handleFileDownload(file.savedName, file.originalName)}
                        >
                          {file.originalName}
                          <span className="file-btn-icon">↓</span>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}

        {loading && <p className="loading-text">불러오는 중...</p>}
        {hasMore && !loading && (
          <button className="faq-list__load-more-btn" onClick={fetchFAQList}>
            더보기
          </button>
        )}
        {faqList.length > 0 && !hasMore && (
          <p className="faq-list__no-more-text">모든 FAQ를 확인했습니다.</p>
        )}
        {faqList.length === 0 && <p className="faq-list__no-faq">등록된 FAQ가 없습니다.</p>}
      </div>
    </div>
  );
};

export default FAQList;
